import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiClipboard, FiFileText, FiHeart, FiUsers } from 'react-icons/fi';
import { useAuth } from './AuthContext';
import { getApiBase } from '../utils/runtime';
import { useCareCoordinator } from '../care/CareCoordinatorContext';
import { useLanguage } from '../i18n/LanguageContext';
import {
  ConsentPanel,
  FeedbackPanel,
  HealthProfilePanel,
  RecommendationPanel,
  SubjectSwitcher,
  TimelinePanel
} from './care/CarePanels';
import './PatientDashboard.css';

const API_BASE = getApiBase();

function PatientDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const {
    activeSubject,
    activeSubjectId,
    auditLog,
    consents,
    experience,
    feedback,
    healthProfileCompleteness,
    managedSubjects,
    observations,
    recommendations,
    setActiveSubjectId,
    updateHealthProfile,
    addConsent,
    revokeConsent,
    addFeedback,
    recordRecommendationAction
  } = useCareCoordinator();
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const isFrench = language === 'fr';

  useEffect(() => {
    if (!user?.user_id) return;
    setLoadingAppointments(true);
    fetch(`${API_BASE}/patients/${user.user_id}/appointments`)
      .then((res) => res.json())
      .then((data) => setAppointments(Array.isArray(data) ? data : []))
      .catch(() => setAppointments([]))
      .finally(() => setLoadingAppointments(false));
  }, [user]);

  const upcomingAppointment = useMemo(() => {
    return [...appointments]
      .filter((appointment) => new Date(appointment.start_utc || appointment.start_time) > new Date())
      .sort((a, b) => new Date(a.start_utc || a.start_time) - new Date(b.start_utc || b.start_time))[0] || null;
  }, [appointments]);

  const handleRecommendationAction = (recommendation, action) => {
    recordRecommendationAction(recommendation, action);
    if (action === 'defer') return;
    if (recommendation.actionType === 'booking') {
      navigate('/booking', { state: { conciergePrefill: recommendation.bookingPrefill || null } });
      return;
    }
    if (recommendation.actionType === 'profile' || recommendation.actionType === 'record') {
      navigate('/patient/dossier');
      return;
    }
  };

  return (
    <div className="patient-dashboard">
      <section className="patient-dashboard__hero">
        <div>
          <span className="patient-dashboard__eyebrow">{experience === 'delegate' ? (isFrench ? 'Espace de soins delegues' : 'Delegated care workspace') : (isFrench ? 'Espace patient' : 'Patient care workspace')}</span>
          <h1>{isFrench ? 'Bon retour' : 'Welcome back'}, {user?.first_name || user?.given_name || (isFrench ? 'a vous' : 'there')}.</h1>
          <p>
            {experience === 'delegate'
              ? (isFrench ? 'Vous pouvez gerer les prochaines etapes approuvees pour le beneficiaire choisi ci-dessous.' : 'You can manage consent-approved next steps for the beneficiary selected below.')
              : (isFrench ? 'Votre tableau de bord regroupe maintenant le profil de sante, les suivis IA, les autorisations et la continuite des services.' : 'Your dashboard brings together profile completion, AI follow-up, delegated care controls, and service continuity.')}
          </p>
        </div>
        <div className="patient-dashboard__hero-card">
          <div className="patient-dashboard__hero-card-stat">
            <strong>{healthProfileCompleteness.percentage}%</strong>
            <div>
              <span className="patient-dashboard__hero-card-label">{isFrench ? 'Profil de sante' : 'Health profile'}</span>
              <p>{isFrench ? 'complete a ce jour' : 'completed so far'}</p>
            </div>
          </div>
          <div className="patient-dashboard__hero-progress" aria-hidden="true">
            <div
              className="patient-dashboard__hero-progress-bar"
              style={{ width: `${healthProfileCompleteness.percentage}%` }}
            />
          </div>
          <div className="patient-dashboard__hero-card-footer">
            <span>{isFrench ? 'Ajoutez vos details medicaux, preferences et contacts d urgence.' : 'Add your medical details, care preferences, and emergency contacts.'}</span>
            <button type="button" onClick={() => navigate('/patient/dossier')}>{isFrench ? 'Completer le profil' : 'Complete profile'}</button>
          </div>
        </div>
      </section>

      <SubjectSwitcher managedSubjects={managedSubjects} activeSubjectId={activeSubjectId} onChange={setActiveSubjectId} />

      <section className="patient-dashboard__cards">
        <article className="patient-dashboard__card">
          <span className="patient-dashboard__card-icon"><FiHeart /></span>
          <h3>{isFrench ? 'Contexte de soins actif' : 'Active care context'}</h3>
          <p>{activeSubject?.displayName || (isFrench ? 'Votre parcours de soins' : 'Your care plan')}</p>
          <button type="button" onClick={() => navigate('/patient/dossier')}>{isFrench ? 'Voir le dossier de soins' : 'Review care record'}</button>
        </article>
        <article className="patient-dashboard__card">
          <span className="patient-dashboard__card-icon"><FiCalendar /></span>
          <h3>{isFrench ? 'Services a venir' : 'Upcoming services'}</h3>
          <p>{loadingAppointments ? (isFrench ? 'Chargement des rendez-vous...' : 'Loading appointments...') : (upcomingAppointment ? (isFrench ? `${upcomingAppointment.service_name} est prevu ensuite` : `${upcomingAppointment.service_name} is scheduled next`) : (isFrench ? 'Aucun service reserve pour le moment.' : 'No upcoming service has been booked yet.'))}</p>
          <button type="button" onClick={() => navigate('/patient/rendez-vous')}>{isFrench ? 'Voir les rendez-vous' : 'View appointments'}</button>
        </article>
        <article className="patient-dashboard__card">
          <span className="patient-dashboard__card-icon"><FiUsers /></span>
          <h3>{isFrench ? 'Acces delegue' : 'Delegated care'}</h3>
          <p>{consents.filter((entry) => entry.status === 'active').length} {isFrench ? 'autorisation(s) active(s)' : 'active consent relationship(s)'}</p>
          <button type="button" onClick={() => window.scrollTo({ top: document.body.scrollHeight * 0.45, behavior: 'smooth' })}>{isFrench ? 'Gerer les autorisations' : 'Manage consent'}</button>
        </article>
        <article className="patient-dashboard__card">
          <span className="patient-dashboard__card-icon"><FiClipboard /></span>
          <h3>{isFrench ? 'Observations recentes' : 'Recent observations'}</h3>
          <p>{observations.length ? (isFrench ? `${observations.length} observation(s) structuree(s) liee(s) a ce contexte.` : `${observations.length} structured observation(s) are linked to this care context.`) : (isFrench ? 'Aucune observation enregistree pour le moment.' : 'No observations have been recorded yet.')}</p>
          <button type="button" onClick={() => navigate('/patient/dossier')}>{isFrench ? 'Voir l activite' : 'Open timeline'}</button>
        </article>
      </section>

      <section className="patient-dashboard__legacy">
        <button type="button" onClick={() => navigate('/patient/rendez-vous')}>{isFrench ? 'Rendez-vous' : 'Appointments'}</button>
        <button type="button" onClick={() => navigate('/patient/dossier')}>{isFrench ? 'Dossier de sante' : 'Health record'}</button>
        <button type="button" onClick={() => navigate('/patient/profil')}>{isFrench ? 'Profil' : 'Profile'}</button>
        <button type="button" onClick={() => navigate('/patient/waitinglistpage')}>{isFrench ? 'Liste d attente' : 'Waiting list'}</button>
        <button type="button" onClick={() => navigate('/patient/factures')}>{isFrench ? 'Factures' : 'Invoices'}</button>
      </section>

      <div className="patient-dashboard__grid">
        <div className="patient-dashboard__main">
          <RecommendationPanel title={isFrench ? 'Prochaines etapes du concierge IA' : 'AI concierge next steps'} recommendations={recommendations} onAction={handleRecommendationAction} />
          <HealthProfilePanel subject={activeSubject} completeness={healthProfileCompleteness} onSave={(values) => updateHealthProfile(activeSubject.id, values)} />
          <FeedbackPanel onSubmit={(payload) => addFeedback({ ...payload, linkedId: upcomingAppointment?.appointment_id || null })} />
        </div>
        <div className="patient-dashboard__side">
          <ConsentPanel consents={consents} onCreate={addConsent} onRevoke={revokeConsent} />
          <TimelinePanel observations={observations} auditLog={auditLog} />
          <section className="care-panel">
            <div className="care-panel__header">
              <div>
                <h3>{isFrench ? 'Rappels utiles' : 'Helpful reminders'}</h3>
                <p>{isFrench ? 'Des rappels ajoutes sans modifier les parcours de reservation, de jumelage ou de planification.' : 'Supportive reminders layered on top of the existing booking, matching, and scheduling flows.'}</p>
              </div>
            </div>
            <div className="care-grid">
              <div className="care-card">
                <strong>{isFrench ? 'Profil de sante' : 'Health profile'}</strong>
                <p>{healthProfileCompleteness.percentage < 85 ? (isFrench ? 'Ajouter plus de renseignements ameliorera les prochaines suggestions de soins.' : 'Adding more detail will improve future care suggestions.') : (isFrench ? 'Votre profil principal est en bonne voie.' : 'Your core profile is in good shape.')}</p>
              </div>
              <div className="care-card">
                <strong>{isFrench ? 'Prochain service' : 'Upcoming service'}</strong>
                <p>{upcomingAppointment ? (isFrench ? `Suivant : ${upcomingAppointment.service_name}` : `Next: ${upcomingAppointment.service_name}`) : (isFrench ? 'Aucun service a venir pour le moment.' : 'No upcoming booking yet.')}</p>
              </div>
              <div className="care-card">
                <strong>{isFrench ? 'Commentaires' : 'Feedback'}</strong>
                <p>{feedback.length ? (isFrench ? `${feedback.length} retour(s) deja recueilli(s).` : `${feedback.length} feedback item(s) captured.`) : (isFrench ? 'Le formulaire de retour sera pret apres le prochain service.' : 'A feedback prompt will be ready after the next service.')}</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PatientDashboard;
